import { useState } from 'react';
import { Trash2, Sparkles } from 'lucide-react';

interface FlagConfirmModalProps {
    itemName: string;
    flagType: 'ToBeDestroyed' | 'NewProduct';
    action: 'set' | 'remove';
    onConfirm: (reason?: string) => void;
    onCancel: () => void;
}

const FLAG_LABELS = {
    ToBeDestroyed: { label: 'To Be Destroyed', icon: Trash2, color: 'red' },
    NewProduct: { label: 'New Product', icon: Sparkles, color: 'blue' },
};

export function FlagConfirmModal({ itemName, flagType, action, onConfirm, onCancel }: FlagConfirmModalProps) {
    const [reason, setReason] = useState('');
    const config = FLAG_LABELS[flagType];
    const Icon = config.icon;

    const isSet = action === 'set';
    const title = isSet ? `Mark as "${config.label}"` : `Remove "${config.label}"`;
    const buttonColor = isSet
        ? config.color === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
        : 'bg-gray-600 hover:bg-gray-700';

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <div className="flex items-start gap-3">
                    <div className={`rounded-full p-2 ${config.color === 'red' ? 'bg-red-100' : 'bg-blue-100'}`}>
                        <Icon className={`h-5 w-5 ${config.color === 'red' ? 'text-red-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                        <p className="mt-1 text-sm text-gray-600">
                            {isSet
                                ? `Flag "${itemName}" as ${config.label.toLowerCase()}?`
                                : `Remove ${config.label.toLowerCase()} flag from "${itemName}"?`
                            }
                        </p>
                        <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Reason <span className="text-gray-400">(optional)</span>
                            </label>
                            <textarea
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                                placeholder="Add a reason..."
                                rows={2}
                                maxLength={500}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="rounded-md border px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onConfirm(reason.trim() || undefined)}
                        className={`rounded-md px-3 py-1.5 text-sm font-medium text-white ${buttonColor}`}
                    >
                        {isSet ? 'Set Flag' : 'Remove Flag'}
                    </button>
                </div>
            </div>
        </div>
    );
}
